import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { MHContainer } from '../containers/MHContainer';
import { MHGrid, MHGridItem, type MHGridPreset } from './MHGrid';
import { MHStack } from './MHStack';

const PAGE_PADDING = {
  mobile: 'px-5 py-6',
  md: 'md:px-7 md:py-7',
  lg: 'lg:px-8 lg:py-8',
} as const;

const mhPageDensity = cva('', {
  variants: {
    density: {
      comfortable: 'gap-6 md:gap-8',
      compact: 'gap-4 md:gap-6',
    },
  },
  defaultVariants: {
    density: 'comfortable',
  },
});

export interface MHPageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mhPageDensity> {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  rightRail?: React.ReactNode;
  railPreset?: Extract<MHGridPreset, 'split' | 'wide'>;
  maxWidth?: 'dash' | 'table' | 'content';
}

const MHPage = React.forwardRef<HTMLDivElement, MHPageProps>(
  (
    {
      className,
      title,
      description,
      actions,
      rightRail,
      railPreset = 'split',
      density,
      maxWidth = 'dash',
      children,
      ...props
    },
    ref
  ) => {
    const containerClasses = cn(
      'mx-auto w-full',
      PAGE_PADDING.mobile,
      PAGE_PADDING.md,
      PAGE_PADDING.lg,
      maxWidth === 'dash' && 'max-w-dash',
      maxWidth === 'table' && 'max-w-table',
      maxWidth === 'content' && 'max-w-content'
    );

    return (
      <MHContainer
        ref={ref}
        type="page"
        className={cn(containerClasses, className)}
        {...props}
      >
        <MHStack gap={density === 'compact' ? 24 : 32}>
          <MHStack
            gap={density === 'compact' ? 12 : 16}
            direction="horizontal"
            align="start"
            justify="between"
            className="flex-wrap"
          >
            <div>
              <h1 className="text-h2 font-semibold text-text-primary">{title}</h1>
              {description ? (
                <p className="mt-2 text-body text-text-secondary">{description}</p>
              ) : null}
            </div>
            {actions ? <div>{actions}</div> : null}
          </MHStack>

          {rightRail ? (
            <MHGrid preset={railPreset} gap={density === 'compact' ? 16 : 24}>
              <MHGridItem area="main">{children}</MHGridItem>
              <MHGridItem area="side">{rightRail}</MHGridItem>
            </MHGrid>
          ) : (
            <div>{children}</div>
          )}
        </MHStack>
      </MHContainer>
    );
  }
);

MHPage.displayName = 'MHPage';

export { MHPage, mhPageDensity, PAGE_PADDING };
